<template>
  <div class="invoice-upload-container">
    <el-card>
      <div slot="header" class="clearfix">
        <span>批量上传Invoice PDF</span>
        <el-button 
          style="float: right; padding: 3px 0" 
          type="text"
          @click="clearAll"
        >
          清空列表
        </el-button>
      </div>

      <!-- 月份选择 -->
      <div class="month-selector">
        <el-form :inline="true">
          <el-form-item label="账单月份:">
            <el-date-picker
              v-model="selectedMonth"
              type="month"
              placeholder="选择月份"
              format="yyyy-MM"
              value-format="yyyyMM"
              :clearable="false"
            >
            </el-date-picker>
          </el-form-item>
          <el-form-item>
            <el-tag type="info" size="small">当前选择: {{ selectedMonth ? formatMonth(selectedMonth) : '未选择' }}</el-tag>
          </el-form-item>
        </el-form>
      </div>

      <!-- 文件上传区域 -->
      <el-upload
        ref="upload"
        class="upload-demo"
        drag
        action="#"
        :auto-upload="false"
        :on-change="handleFileChange"
        :on-remove="handleFileRemove"
        :file-list="fileList"
        :limit="20"
        :on-exceed="handleExceed"
        accept=".pdf"
        multiple
      >
        <i class="el-icon-upload"></i>
        <div class="el-upload__text">将PDF文件拖到此处，或<em>点击选择文件</em></div>
        <div class="el-upload__tip" slot="tip">
          支持批量上传，一次最多上传20个PDF文件，单个文件不超过10MB
        </div>
      </el-upload>

      <!-- 文件列表预览 -->
      <div v-if="fileList.length > 0" class="file-list-preview">
        <el-divider content-position="left">待上传文件列表 ({{ fileList.length }})</el-divider>
        <el-table :data="fileList" style="width: 100%">
          <el-table-column prop="name" label="文件名" min-width="200">
            <template slot-scope="scope">
              <i class="el-icon-document"></i> {{ scope.row.name }}
            </template>
          </el-table-column>
          <el-table-column label="文件大小" width="120">
            <template slot-scope="scope">
              {{ formatFileSize(scope.row.size) }}
            </template>
          </el-table-column>
          <el-table-column label="状态" width="100">
            <template slot-scope="scope">
              <el-tag size="small" type="info">待上传</el-tag>
            </template>
          </el-table-column>
          <el-table-column label="操作" width="100">
            <template slot-scope="scope">
              <el-button 
                type="text" 
                size="small" 
                @click="removeFile(scope.$index)"
              >
                移除
              </el-button>
            </template>
          </el-table-column>
        </el-table>
      </div>

      <!-- 上传按钮 -->
      <div class="upload-actions" v-if="fileList.length > 0">
        <el-button 
          type="primary" 
          @click="submitUpload"
          :loading="uploading"
          icon="el-icon-upload"
        >
          开始上传并识别 ({{ fileList.length }}个文件)
        </el-button>
        <el-button @click="clearAll">取消</el-button>
      </div>

      <!-- 上传进度 -->
      <div v-if="uploading" class="upload-progress">
        <el-progress 
          :percentage="uploadProgress" 
          :status="uploadProgress === 100 ? 'success' : ''"
        ></el-progress>
        <p class="progress-text">正在处理文件，请稍候...</p>
      </div>

      <!-- 上传结果 -->
      <div v-if="uploadResults.length > 0" class="upload-results">
        <el-divider content-position="left">
          上传结果 (成功: {{ successCount }}, 失败: {{ failCount }})
        </el-divider>
        
        <el-table 
          :data="uploadResults" 
          style="width: 100%"
          :row-class-name="tableRowClassName"
        >
          <el-table-column type="expand">
            <template slot-scope="props">
              <div class="result-detail">
                <div v-if="props.row.status === 'success' && props.row.data">
                  <el-descriptions :column="2" border size="small">
                    <el-descriptions-item label="Payer ID">
                      {{ props.row.data.payerId }}
                    </el-descriptions-item>
                    <el-descriptions-item label="Invoice ID">
                      {{ props.row.data.invoiceId }}
                    </el-descriptions-item>
                    <el-descriptions-item label="类型">
                      {{ props.row.data.type === 'cn' ? '中文' : '英文' }}
                    </el-descriptions-item>
                    <el-descriptions-item label="子账户数量">
                      {{ props.row.data.SummaryForLinkedAccount ? props.row.data.SummaryForLinkedAccount.length : 0 }}
                    </el-descriptions-item>
                  </el-descriptions>
                  
                  <div v-if="props.row.data.Summary" style="margin-top: 10px;">
                    <strong>总览信息：</strong>
                    <el-tag 
                      v-for="(value, key) in props.row.data.Summary" 
                      :key="key"
                      size="small"
                      style="margin: 5px;"
                    >
                      {{ key }}: {{ value }}
                    </el-tag>
                  </div>
                </div>
                <div v-else-if="props.row.error">
                  <el-alert
                    :title="props.row.error"
                    type="error"
                    :closable="false"
                  ></el-alert>
                </div>
              </div>
            </template>
          </el-table-column>
          
          <el-table-column prop="file_name" label="文件名" min-width="200" />
          
          <el-table-column label="状态" width="100">
            <template slot-scope="scope">
              <el-tag 
                :type="scope.row.status === 'success' ? 'success' : 'danger'"
                size="small"
              >
                {{ scope.row.status === 'success' ? '成功' : '失败' }}
              </el-tag>
            </template>
          </el-table-column>
          
          <el-table-column prop="invoice_id" label="Invoice ID" width="150" />
          
          <el-table-column label="操作" width="150">
            <template slot-scope="scope">
              <el-button 
                v-if="scope.row.status === 'success'"
                type="text" 
                size="small"
                @click="viewDetail(scope.row)"
              >
                查看详情
              </el-button>
              <el-button 
                v-if="scope.row.status === 'failed'"
                type="text" 
                size="small"
                @click="retryUpload(scope.row)"
              >
                重试
              </el-button>
            </template>
          </el-table-column>
        </el-table>

        <div class="result-actions">
          <el-button 
            type="success" 
            @click="exportResults"
            icon="el-icon-download"
          >
            导出结果
          </el-button>
          <el-button @click="clearResults">清空结果</el-button>
        </div>
      </div>
    </el-card>

    <!-- 详情对话框 -->
    <el-dialog
      title="Invoice详情"
      :visible.sync="detailDialogVisible"
      width="70%"
      :close-on-click-modal="false"
    >
      <div v-if="currentDetail">
        <el-descriptions :column="2" border>
          <el-descriptions-item label="Payer ID">
            {{ currentDetail.payerId }}
          </el-descriptions-item>
          <el-descriptions-item label="Invoice ID">
            {{ currentDetail.invoiceId }}
          </el-descriptions-item>
          <el-descriptions-item label="类型">
            {{ currentDetail.type === 'cn' ? '中文' : '英文' }}
          </el-descriptions-item>
        </el-descriptions>

        <el-divider content-position="left">总览信息</el-divider>
        <el-table :data="summaryTableData" border size="small">
          <el-table-column prop="key" label="项目" />
          <el-table-column prop="value" label="金额" />
        </el-table>

        <el-divider content-position="left">关联账户</el-divider>
        <el-collapse v-model="activeAccounts">
          <el-collapse-item 
            v-for="(account, index) in currentDetail.SummaryForLinkedAccount" 
            :key="index"
            :name="index"
          >
            <template slot="title">
              <strong>{{ account.Account }}</strong> - {{ account.AccountName }}
            </template>
            
            <el-tabs>
              <el-tab-pane label="汇总">
                <el-table :data="formatAccountData(account.Values)" border size="small">
                  <el-table-column prop="key" label="项目" />
                  <el-table-column prop="value" label="金额" />
                </el-table>
              </el-tab-pane>
              
              <el-tab-pane label="明细">
                <el-table :data="formatAccountData(account.Detail)" border size="small">
                  <el-table-column prop="key" label="项目" />
                  <el-table-column prop="value" label="金额" />
                </el-table>
              </el-tab-pane>
            </el-tabs>
          </el-collapse-item>
        </el-collapse>
      </div>
      
      <span slot="footer" class="dialog-footer">
        <el-button @click="detailDialogVisible = false">关闭</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
export default {
  name: 'InvoiceUpload',
  data() {
    // 默认为上个月
    const lastMonth = new Date();
    lastMonth.setMonth(lastMonth.getMonth() - 1);
    const defaultMonth = lastMonth.getFullYear() + String(lastMonth.getMonth() + 1).padStart(2, '0');
    
    return {
      fileList: [],
      uploading: false,
      uploadProgress: 0,
      uploadResults: [],
      detailDialogVisible: false,
      currentDetail: null,
      activeAccounts: [],
      selectedMonth: defaultMonth
    };
  },
  computed: {
    successCount() {
      return this.uploadResults.filter(r => r.status === 'success').length;
    },
    failCount() {
      return this.uploadResults.filter(r => r.status === 'failed').length;
    },
    summaryTableData() {
      if (!this.currentDetail || !this.currentDetail.Summary) return [];
      return Object.entries(this.currentDetail.Summary).map(([key, value]) => ({
        key,
        value
      }));
    }
  },
  methods: {
    handleFileChange(file, fileList) {
      // 验证文件大小
      const maxSize = 10 * 1024 * 1024; // 10MB
      if (file.size > maxSize) {
        this.$message.error(`文件 ${file.name} 超过10MB限制`);
        this.fileList = fileList.filter(f => f.uid !== file.uid);
        return;
      }
      
      // 验证文件类型
      if (!file.name.toLowerCase().endsWith('.pdf')) {
        this.$message.error(`文件 ${file.name} 不是PDF格式`);
        this.fileList = fileList.filter(f => f.uid !== file.uid);
        return;
      }
      
      this.fileList = fileList;
    },

    handleFileRemove(file, fileList) {
      this.fileList = fileList;
    },

    handleExceed(files, fileList) {
      this.$message.warning(`最多只能上传20个文件，当前已选择${fileList.length}个文件，本次选择了${files.length}个文件`);
    },

    removeFile(index) {
      this.fileList.splice(index, 1);
      this.$refs.upload.clearFiles();
      this.fileList.forEach(file => {
        this.$refs.upload.handleStart(file.raw);
      });
    },

    clearAll() {
      this.fileList = [];
      this.$refs.upload.clearFiles();
    },

    async submitUpload() {
      if (this.fileList.length === 0) {
        this.$message.warning('请先选择文件');
        return;
      }

      if (!this.selectedMonth) {
        this.$message.warning('请选择账单月份');
        return;
      }

      // 创建FormData
      const formData = new FormData();
      this.fileList.forEach(file => {
        formData.append('files', file.raw);
      });
      formData.append('month', this.selectedMonth);

      this.uploading = true;
      this.uploadProgress = 0;
      this.uploadResults = [];

      // 模拟进度
      const progressInterval = setInterval(() => {
        if (this.uploadProgress < 90) {
          this.uploadProgress += 10;
        }
      }, 500);

      try {
        const response = await this.$http.post('/bill/invoice/upload/', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });

        clearInterval(progressInterval);
        this.uploadProgress = 100;

        if (response.data.status === 'success') {
          this.$message.success(response.data.message);
          this.uploadResults = response.data.results;
          
          // 清空文件列表
          this.fileList = [];
          this.$refs.upload.clearFiles();
        } else {
          this.$message.error(response.data.message);
        }
      } catch (error) {
        clearInterval(progressInterval);
        this.$message.error('上传失败: ' + (error.response?.data?.message || error.message));
      } finally {
        this.uploading = false;
        setTimeout(() => {
          this.uploadProgress = 0;
        }, 2000);
      }
    },

    clearResults() {
      this.uploadResults = [];
    },

    viewDetail(row) {
      this.currentDetail = row.data;
      this.detailDialogVisible = true;
      this.activeAccounts = [];
    },

    retryUpload(row) {
      this.$message.info('重试功能开发中...');
    },

    exportResults() {
      // 导出结果为CSV或Excel
      const csvContent = this.uploadResults.map(r => 
        `${r.file_name},${r.status},${r.invoice_id || ''},${r.error || ''}`
      ).join('\n');
      
      const blob = new Blob(['\ufeff' + 'File Name,Status,Invoice ID,Error\n' + csvContent], { 
        type: 'text/csv;charset=utf-8;' 
      });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `upload_results_${Date.now()}.csv`;
      link.click();
      URL.revokeObjectURL(link.href);
    },

    formatFileSize(bytes) {
      if (bytes === 0) return '0 B';
      const k = 1024;
      const sizes = ['B', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
    },

    tableRowClassName({ row }) {
      if (row.status === 'success') {
        return 'success-row';
      } else if (row.status === 'failed') {
        return 'error-row';
      }
      return '';
    },

    formatAccountData(data) {
      if (!data) return [];
      return Object.entries(data).map(([key, value]) => ({
        key,
        value
      }));
    },

    formatMonth(month) {
      if (!month || month.length !== 6) return month;
      return month.substring(0, 4) + '年' + month.substring(4, 6) + '月';
    }
  }
};
</script>

<style scoped>
.invoice-upload-container {
  padding: 20px;
}

.month-selector {
  margin-bottom: 20px;
  padding: 16px 20px;
  background: #FAFAFA;
  border-radius: 8px;
  border: 1px solid #F2F2F7;
}

.upload-demo {
  margin-bottom: 20px;
}

.file-list-preview {
  margin-top: 20px;
}

.upload-actions {
  margin-top: 20px;
  text-align: center;
}

.upload-progress {
  margin-top: 20px;
  padding: 20px;
  background: #FAFAFA;
  border-radius: 8px;
  border: 1px solid #F2F2F7;
}

.progress-text {
  text-align: center;
  margin-top: 10px;
  color: #6E6E73;
  font-size: 13px;
}

.upload-results {
  margin-top: 30px;
}

.result-actions {
  margin-top: 20px;
  text-align: center;
}

.result-detail {
  padding: 20px;
  background: #FAFAFA;
  border-radius: 8px;
}

.el-table >>> .success-row {
  background-color: #F0FBF3;
}

.el-table >>> .error-row {
  background-color: #FFF2F1;
}
</style>
